import React from 'react';
import { Button, Dropdown, Space, Tooltip } from 'antd';
import { MoreOutlined, PlusOutlined } from '@ant-design/icons';
import useIsMobile from '../../hooks/useIsMobile';

/**
 * Page-header actions with an overflow menu.
 *
 * List pages had grown to six or seven buttons sitting side by side above the
 * table — Refresh, Import, Export, Bulk Delete, Settings, Create... On a phone
 * that wrapped into three rows before any data was visible. The one action
 * people came to perform stays a real button; the rest collapse behind a
 * three-dot menu.
 *
 * `primary`  - the single most important action, always visible.
 * `actions`  - everything else, as {key,label,icon,onClick,danger,disabled}.
 * `maxInline`- how many secondary actions may stay inline on a wide screen
 *              (default 2). On mobile every secondary action goes to the menu.
 *
 * An action with `keepInline: true` stays a button on desktop no matter what,
 * for things like Refresh where the icon alone is self-explanatory.
 */
const PageActions = ({ primary, actions = [], maxInline = 2 }) => {
  const isMobile = useIsMobile(991);
  const isPhone = useIsMobile(576);
  const available = actions.filter(Boolean);

  // On a phone nothing but the primary action stays out; on desktop keep the
  // first `maxInline` (plus anything explicitly pinned) and hide the rest.
  const pinned = isMobile ? [] : available.filter((a) => a.keepInline);
  const rest = available.filter((a) => !pinned.includes(a));
  const inline = isMobile ? [] : rest.slice(0, Math.max(0, maxInline - pinned.length));
  const overflow = isMobile ? available : rest.slice(inline.length);

  const menuItems = overflow.map((a) => ({
    key: a.key,
    label: a.label,
    icon: a.icon,
    danger: a.danger,
    disabled: a.disabled,
  }));

  const handleMenuClick = ({ key }) => {
    const action = overflow.find((a) => a.key === key);
    if (action && action.onClick) action.onClick();
  };

  const renderButton = (a) => {
    const button = (
      <Button
        key={a.key}
        icon={a.icon}
        onClick={a.onClick}
        danger={a.danger}
        disabled={a.disabled}
        loading={a.loading}
      >
        {a.iconOnly ? null : a.label}
      </Button>
    );
    return a.iconOnly ? (
      <Tooltip key={a.key} title={a.label}>
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <Space size={8} wrap={false} className="page-actions">
      {[...pinned, ...inline].map(renderButton)}

      {menuItems.length > 0 && (
        <Dropdown
          menu={{ items: menuItems, onClick: handleMenuClick }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button icon={<MoreOutlined />} aria-label="More actions" />
        </Dropdown>
      )}

      {primary && (
        /* On a phone this is a bare "+": the label would push the row off the
           edge, and a plus reads as "create" without one. Between phone and
           desktop the shortLabel keeps a word on the button. */
        <Tooltip title={isPhone ? primary.label : undefined}>
          <Button
            type={primary.type || 'primary'}
            icon={isPhone ? <PlusOutlined /> : primary.icon}
            onClick={primary.onClick}
            disabled={primary.disabled}
            loading={primary.loading}
            className={primary.className}
            aria-label={primary.label}
          >
            {isPhone
              ? null
              : isMobile && primary.shortLabel
                ? primary.shortLabel
                : primary.label}
          </Button>
        </Tooltip>
      )}
    </Space>
  );
};

export default PageActions;
